import React, { useState, useEffect } from 'react';
import { X, Share, Plus, Home } from 'lucide-react';

const PWAInstallPrompt = () => {
  const [showInstructions, setShowInstructions] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInPWA, setIsInPWA] = useState(false);
  const [hasPromptBeenDismissed, setHasPromptBeenDismissed] = useState(false);

  useEffect(() => {
    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    // Detect if already in PWA mode
    const inPWA = window.matchMedia('(display-mode: standalone)').matches || 
                  window.navigator.standalone === true;
    
    // Check if user has previously dismissed the prompt
    const dismissed = localStorage.getItem('pwa-install-dismissed') === 'true';
    
    setIsIOS(iOS);
    setIsInPWA(inPWA);
    setHasPromptBeenDismissed(dismissed);

    // Show prompt if iOS user, not in PWA, hasn't dismissed, and has used app for a bit
    if (iOS && !inPWA && !dismissed) {
      // Add a small delay to let user interact with the app first
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 3000); // Show after 3 seconds

      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismiss = (rememberChoice = false) => {
    setShowPrompt(false);
    if (rememberChoice) {
      localStorage.setItem('pwa-install-dismissed', 'true');
    }
  };

  const handleInstallClick = () => {
    setShowInstructions(true);
  };

  if (!showPrompt || !isIOS || isInPWA) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-accent-600 to-brand-500 text-white p-4 shadow-lg z-50 animate-slide-up">
      <div className="max-w-md mx-auto">
        {!showInstructions ? (
          // Initial prompt
          <>
            <div className="flex justify-between items-start mb-2">
              <div className="flex-1">
                <h3 className="font-bold text-sm">📱 Install FlowFrame</h3>
                <p className="text-xs opacity-90">
                  Add to your home screen for the best experience!
                </p>
              </div>
              <button
                onClick={() => handleDismiss(false)}
                className="text-white hover:text-gray-200 ml-2"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={handleInstallClick}
                className="flex-1 bg-white text-accent-600 px-3 py-2 rounded font-medium text-sm hover:bg-gray-100 transition-colors"
              >
                Show Me How
              </button>
              <button
                onClick={() => handleDismiss(true)}
                className="px-3 py-2 text-sm border border-white border-opacity-50 rounded hover:bg-white hover:bg-opacity-10 transition-colors"
              >
                Not Now
              </button>
            </div>
          </>
        ) : (
          // Quick instructions
          <>
            <div className="mb-3">
              <h3 className="font-bold text-sm mb-2">📱 How to Install:</h3>
              <div className="space-y-1 text-xs">
                <div className="flex items-center">
                  <span className="mr-2">1.</span>
                  <Share className="w-3 h-3 mr-1" />
                  <span>Tap the Share button below</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-2">2.</span>
                  <Plus className="w-3 h-3 mr-1" />
                  <Home className="w-3 h-3 mr-1" />
                  <span>Choose "Add to Home Screen"</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-2">3.</span>
                  <span>Tap "Add" to confirm</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowPrompt(false)}
              className="w-full bg-white text-accent-600 px-3 py-2 rounded font-medium text-sm hover:bg-gray-100 transition-colors"
            >
              Got it!
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PWAInstallPrompt;